import type { Sticker } from './stickers'

import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'
import { useStickersStore } from './stickers'

export interface PersonaPreset {
  id: number
  name: string
  avatar: string
  birthday?: string
  gender: 'male' | 'female' | 'unspecified'
  persona?: string
  appliedChats?: string[]
  stickers?: Sticker[] // 用户可用的表情包（旧，兼容）
  stickerPackIds?: string[] // 用户挂载的表情包组
}

/**
 * 用户人格管理 Store
 * 管理用户的多个人格预设
 */
export const useUserPersonaStore = defineStore('userPersona', () => {
  // 使用自定义图片作为默认用户头像
  const defaultAvatar = '/USER.jpg'

  // ✨ 人格预设列表 - 迁移到 IndexedDB（含头像 base64 数据，可能很大）
  const personaPresetsStorage = useIndexedDBStorage<PersonaPreset[]>('user/persona-presets', [], {
    debounceMs: 300,
  })
  const personaPresets = personaPresetsStorage.data

  // 默认人格ID（数字很小，留在 localStorage）
  const defaultPersonaId = useLocalStorage<number | null>('user/default-persona-id', null)

  // 等待存储加载完成
  const personaStorageLoaded = ref(false)
  const personaStorageFailed = ref(false)
  const personaStorageLoadedPromise = waitForStorage(personaPresetsStorage)
    .then(() => {
      personaStorageLoaded.value = true
      console.debug('✅ 用户人格存储加载完成（IndexedDB）')
      schedulePersonaStickerMigration()
    })
    .catch((err) => {
      // 关键路径兜底：IndexedDB 被锁/损坏时不要拖垮 send()，让下游 await 继续。
      // 这里只标记 failed，不把 loaded 置 true —— 避免 watch 把已有的 defaultPersonaId
      // 误判为"列表为空"后清掉。下游 waitForPersonaLoad 会正常 resolve。
      console.error('❌ 用户人格存储加载失败，将使用内存默认值:', err)
      personaStorageFailed.value = true
    })

  async function waitForPersonaLoad() {
    await personaStorageLoadedPromise
  }

  // 将旧的 persona.stickers[] 迁移为 StickerPack
  function schedulePersonaStickerMigration() {
    const stickersStore = useStickersStore()

    if (stickersStore.stickersStorageLoaded) {
      migratePersonaStickers(stickersStore)
    }
    else {
      const stop = watch(() => stickersStore.stickersStorageLoaded, (loaded) => {
        if (loaded) {
          migratePersonaStickers(stickersStore)
          stop()
        }
      })
    }
  }

  function migratePersonaStickers(stickersStore: ReturnType<typeof useStickersStore>) {
    for (const persona of personaPresets.value) {
      if (persona.stickers?.length && (!persona.stickerPackIds || persona.stickerPackIds.length === 0)) {
        try {
          const packName = persona.name ? `${persona.name}的表情包` : '我的表情包'
          const packId = stickersStore.createPack(packName)
          for (const sticker of persona.stickers) {
            stickersStore.addStickerToPack({ name: sticker.name, url: sticker.url }, packId)
          }
          updatePersona(persona.id, { stickerPackIds: [packId], stickers: [] })
          console.info(`✅ 已将人格「${persona.name}」的旧表情包迁移为表情包组「${packName}」`)
        }
        catch (e) {
          console.error('迁移人格表情包失败:', e)
        }
      }
    }
  }

  // 计算属性：获取默认人格（纯读，不做副作用）
  const defaultPersona = computed(() => {
    if (defaultPersonaId.value === null)
      return personaPresets.value[0] ?? null

    const persona = personaPresets.value.find(p => p.id === defaultPersonaId.value)
    if (persona)
      return persona

    // 找不到对应的人格（可能被删除了），fallback 到列表首个
    return personaPresets.value[0] ?? null
  })

  // 自修复：当 defaultPersonaId 为空但已有列表，或者指向一个不存在的 id，
  // 自动把默认人格 id 对齐到列表首个，避免 prompt 链路上读到 null。
  // ⚠️ 必须 storage 成功加载后才修复，否则 IndexedDB 失败分支下 presets 仍是初始空数组，
  // 会把用户已有的 defaultPersonaId 错误清零。
  watch(
    [personaPresets, defaultPersonaId, personaStorageLoaded],
    ([presets, currentId, loaded]) => {
      if (!loaded)
        return
      if (presets.length === 0) {
        if (currentId !== null)
          defaultPersonaId.value = null
        return
      }
      if (currentId === null || !presets.some(p => p.id === currentId)) {
        defaultPersonaId.value = presets[0].id
      }
    },
    { immediate: true },
  )

  // 计算属性：用户显示名称
  const userName = computed(() => defaultPersona.value?.name || '用户名')

  // 计算属性：用户头像
  const userAvatar = computed(() => defaultPersona.value?.avatar || defaultAvatar)

  /**
   * 创建新的人格预设
   */
  function createPersona(persona?: Partial<PersonaPreset>): PersonaPreset {
    const newId = personaPresets.value.length > 0
      ? Math.max(...personaPresets.value.map(p => p.id)) + 1
      : 1

    const newPersona: PersonaPreset = {
      id: newId,
      name: persona?.name || '新的人格',
      avatar: persona?.avatar || defaultAvatar,
      birthday: persona?.birthday || '',
      gender: persona?.gender || 'unspecified',
      persona: persona?.persona || '',
      appliedChats: persona?.appliedChats || [],
      stickers: persona?.stickers || [],
      stickerPackIds: persona?.stickerPackIds || [],
    }

    // 创建新数组以触发 useLocalStorage 的保存
    personaPresets.value = [...personaPresets.value, newPersona]

    // 如果这是第一个人格，自动设为默认
    if (personaPresets.value.length === 1) {
      defaultPersonaId.value = newId
    }

    return newPersona
  }

  /**
   * 更新人格预设
   */
  function updatePersona(id: number, updates: Partial<PersonaPreset>) {
    const index = personaPresets.value.findIndex(p => p.id === id)
    if (index !== -1) {
      // 创建新数组以触发 useLocalStorage 的保存
      personaPresets.value = personaPresets.value.map((p, i) =>
        i === index ? { ...p, ...updates } : p,
      )
    }
  }

  /**
   * 删除人格预设
   */
  async function deletePersona(id: number) {
    // 创建新数组以触发 useLocalStorage 的保存
    personaPresets.value = personaPresets.value.filter(p => p.id !== id)

    // 如果删除的是默认人格，清除默认设置
    if (defaultPersonaId.value === id) {
      defaultPersonaId.value = null
    }

    // 解绑所有引用了该人格的联系人，避免 UI 显示空白 / 设置对话框显示死 ID。
    // 动态 import 打破 user-persona ↔ contacts 的模块顶层循环；Pinia store 单例化后
    // 第二次调用几乎零开销。先收集再更新，避免边遍历 contactsList computed 边写。
    // boundPersonaId 类型为 number | null，不做 Number() 转换（null 会被误匹配成 0）。
    try {
      const { useContactsStore } = await import('./contacts')
      const contactsStore = useContactsStore()
      const affectedIds = contactsStore.contactsList
        .filter((contact: { boundPersonaId?: number | null }) => contact.boundPersonaId === id)
        .map((contact: { id: string }) => contact.id)
      for (const contactId of affectedIds) {
        contactsStore.updateContact(contactId, { boundPersonaId: null })
      }
    }
    catch (e) {
      console.error('解绑联系人的 boundPersonaId 失败:', e)
    }
  }

  /**
   * 设置默认人格
   */
  function setDefaultPersona(id: number) {
    const persona = personaPresets.value.find(p => p.id === id)
    if (persona) {
      defaultPersonaId.value = id
    }
  }

  /**
   * 获取人格预设
   */
  function getPersona(id: number | string | null | undefined): PersonaPreset | undefined {
    if (id == null)
      return undefined
    if (typeof id === 'string' && id.trim() === '')
      return undefined
    const normalized = Number(id)
    if (!Number.isFinite(normalized))
      return undefined
    return personaPresets.value.find(p => p.id === normalized)
  }

  /**
   * 获取人格的本地化性别显示字符串。
   * 统一替换 chat.ts / backgroundActivity.ts / useEnhancedPrompt.ts 里三份重复实现。
   *
   * @param persona 人格对象，允许 null/undefined
   * @param unspecifiedText `unspecified` 时返回的字符串，默认空串
   */
  function getLocalizedGender(
    persona: { gender?: 'male' | 'female' | 'unspecified' } | null | undefined,
    unspecifiedText = '',
  ): string {
    const g = persona?.gender
    if (g === 'male')
      return '男'
    if (g === 'female')
      return '女'
    return unspecifiedText
  }

  /**
   * 调试：获取当前状态
   */
  function debugState() {
    return {
      personaPresetsCount: personaPresets.value.length,
      personaPresets: personaPresets.value,
      defaultPersonaId: defaultPersonaId.value,
      defaultPersona: defaultPersona.value,
      userName: userName.value,
      userAvatar: userAvatar.value,
    }
  }

  /**
   * 解析联系人的有效人格（绑定人格 → 全局默认人格）
   */
  function resolvePersonaForContact(contact: { boundPersonaId?: number | null } | null | undefined): PersonaPreset | null {
    if (contact?.boundPersonaId != null) {
      const bound = getPersona(contact.boundPersonaId)
      if (bound)
        return bound
    }
    return defaultPersona.value
  }

  /**
   * 获取联系人对应的"用户侧"表情包组 ID 列表。
   * 三个表情面板（StickerPanel / UserStickerPanel / StickerSuggestion）原本各自重复解析，
   * 统一到这里避免三处实现漂移。
   */
  function getEffectiveUserStickerPackIds(contact: { boundPersonaId?: number | null } | null | undefined): string[] {
    const persona = resolvePersonaForContact(contact)
    return persona?.stickerPackIds || []
  }

  return {
    // State
    personaPresets,
    defaultPersonaId,
    defaultAvatar,
    personaStorageLoaded,
    personaStorageFailed,

    // Computed
    defaultPersona,
    userName,
    userAvatar,

    // Actions
    createPersona,
    updatePersona,
    deletePersona,
    setDefaultPersona,
    getPersona,
    getLocalizedGender,
    resolvePersonaForContact,
    getEffectiveUserStickerPackIds,
    waitForPersonaLoad,
    debugState,
  }
})
